import { Component, Input, OnChanges } from "@angular/core";
import { FrontierPoint } from "../models/experience.model";

@Component({
  selector: "app-exp-frontier-chart",
  standalone: true,
  imports: [],
  template: `
    <div class="frontier-chart chart-wrap">
      <svg
        [attr.viewBox]="'0 0 ' + width + ' ' + height"
        preserveAspectRatio="none"
      >
        <g class="grid">
          @for (y of yLines; track y) {
            <line
              [attr.x1]="padL"
              [attr.x2]="width - padR"
              [attr.y1]="y"
              [attr.y2]="y"
              stroke="var(--border)"
              stroke-dasharray="2 3"
              opacity="0.5"
            />
          }
        </g>
        <g class="axis">
          @for (t of yTicks; track t) {
            <text
              [attr.x]="padL - 6"
              [attr.y]="t.y"
              text-anchor="end"
              dominant-baseline="middle"
              fill="var(--text-dim)"
              style="font-size:11px"
            >
              {{ t.label }}
            </text>
          }
          @for (t of xTicks; track t) {
            <text
              [attr.x]="t.x"
              [attr.y]="height - padB + 16"
              text-anchor="middle"
              fill="var(--text-dim)"
              style="font-size:11px"
            >
              {{ t.label }}
            </text>
          }
        </g>
        @if (unconstrainedPath) {
          <path class="frontier-curve-uc" [attr.d]="unconstrainedPath" />
        }
        @if (curvePath) {
          <path class="frontier-curve" [attr.d]="curvePath" />
        }
        @for (d of assetDots; track d) {
          <circle
            class="asset-dot"
            [attr.cx]="d.x"
            [attr.cy]="d.y"
            r="3.5"
          ></circle>
        }
        @for (d of portfolioDots; track d) {
          <g>
            <circle
              class="portfolio-dot"
              [attr.cx]="d.x"
              [attr.cy]="d.y"
              r="5"
            ></circle>
            @if (d.label) {
              <text
                [attr.x]="d.x + 8"
                [attr.y]="d.y - 6"
                fill="var(--text)"
                style="font-size:11px"
              >
                {{ d.label }}
              </text>
            }
          </g>
        }
        @for (d of tangencyDots; track d) {
          <circle
            class="tangency-dot"
            [attr.cx]="d.x"
            [attr.cy]="d.y"
            r="5"
          ></circle>
        }
      </svg>
      <div class="frontier-legend">
        <div class="li">
          <span class="sw" style="background:#7C5CFF"></span>Efficient frontier
        </div>
        <div class="li">
          <span class="sw" style="background:#8A90A3"></span>Unconstrained
        </div>
        <div class="li">
          <span
            class="sw"
            style="background:#A78BFF; height:8px; width:8px; border-radius:50%"
          ></span
          >Portfolio
        </div>
        <div class="li">
          <span
            class="sw"
            style="background:#00E5A0; height:8px; width:8px; border-radius:50%"
          ></span
          >Tangency
        </div>
      </div>
    </div>
  `,
})
export class FrontierChartComponent implements OnChanges {
  @Input() frontier: FrontierPoint[] = [];
  @Input() unconstrained: FrontierPoint[] = [];
  @Input() assets: FrontierPoint[] = [];
  @Input() portfolios: FrontierPoint[] = [];
  @Input() width = 760;
  @Input() height = 320;
  @Input() xFormat: (v: number) => string = (v) => (v * 100).toFixed(1) + "%";
  @Input() yFormat: (v: number) => string = (v) => (v * 100).toFixed(1) + "%";

  padL = 50;
  padR = 18;
  padT = 14;
  padB = 30;

  curvePath = "";
  unconstrainedPath = "";
  assetDots: Array<{ x: number; y: number; label?: string }> = [];
  portfolioDots: Array<{ x: number; y: number; label?: string }> = [];
  tangencyDots: Array<{ x: number; y: number }> = [];
  yLines: number[] = [];
  yTicks: Array<{ y: number; label: string }> = [];
  xTicks: Array<{ x: number; label: string }> = [];

  ngOnChanges(): void {
    const all = [
      ...this.frontier,
      ...this.unconstrained,
      ...this.assets,
      ...this.portfolios,
    ];
    if (!all.length) {
      this.curvePath = "";
      this.unconstrainedPath = "";
      this.assetDots = [];
      this.portfolioDots = [];
      this.tangencyDots = [];
      this.yLines = [];
      this.xTicks = [];
      this.yTicks = [];
      return;
    }
    const xMin = Math.min(...all.map((p) => p.vol));
    const xMax = Math.max(...all.map((p) => p.vol));
    const yMin = Math.min(...all.map((p) => p.ret));
    const yMax = Math.max(...all.map((p) => p.ret));
    const xPad = (xMax - xMin || 1) * 0.08;
    const yPad = (yMax - yMin || 1) * 0.12;
    const xLo = xMin - xPad;
    const xHi = xMax + xPad;
    const yLo = yMin - yPad;
    const yHi = yMax + yPad;
    const plotW = this.width - this.padL - this.padR;
    const plotH = this.height - this.padT - this.padB;
    const sx = (x: number) => this.padL + ((x - xLo) / (xHi - xLo)) * plotW;
    const sy = (y: number) =>
      this.padT + plotH - ((y - yLo) / (yHi - yLo)) * plotH;

    this.curvePath = this.frontier.length
      ? this.frontier
          .map(
            (p, i) =>
              `${i === 0 ? "M" : "L"}${sx(p.vol).toFixed(2)},${sy(p.ret).toFixed(2)}`,
          )
          .join(" ")
      : "";
    this.unconstrainedPath = this.unconstrained.length
      ? this.unconstrained
          .map(
            (p, i) =>
              `${i === 0 ? "M" : "L"}${sx(p.vol).toFixed(2)},${sy(p.ret).toFixed(2)}`,
          )
          .join(" ")
      : "";
    this.assetDots = this.assets.map((p) => ({
      x: sx(p.vol),
      y: sy(p.ret),
      label: p.label,
    }));
    this.portfolioDots = this.portfolios
      .filter((p) => p.kind !== "tangency")
      .map((p) => ({ x: sx(p.vol), y: sy(p.ret), label: p.label }));
    this.tangencyDots = this.portfolios
      .filter((p) => p.kind === "tangency")
      .map((p) => ({ x: sx(p.vol), y: sy(p.ret) }));

    const steps = 4;
    this.yLines = [];
    this.yTicks = [];
    for (let i = 0; i <= steps; i++) {
      const val = yLo + ((yHi - yLo) * i) / steps;
      const y = sy(val);
      this.yLines.push(y);
      this.yTicks.push({ y, label: this.yFormat(val) });
    }
    this.xTicks = [];
    for (let i = 0; i <= steps; i++) {
      const val = xLo + ((xHi - xLo) * i) / steps;
      this.xTicks.push({ x: sx(val), label: this.xFormat(val) });
    }
  }
}
